import { useTranslation } from "react-i18next";
import { GeolocationService } from "@/services/geolocationService";
import { ContractorPanelVisitService } from "../services/contractorPanelVisitService";
import { useToast } from "@/components/ui/use-toast";
import { DateTime } from "luxon";
import { useOfflineAwareMutation } from "@/lib/hooks/useOfflineAwareMutation";
import { useCachedMutation } from "@/hooks/useCachedMutation";
import { Module } from "@/constants";
import { ReadContractorVisit } from "@/types/contractorVisit";
import { getGeolocationErrorMessage } from "../utils/getGeolocationErrorMessage";
import { getSuccessMessageWithGeolocation } from "../utils/getSuccessMessageWithGeolocation";
import { useDeviceInfo } from "@/lib/hooks/useDeviceType";
import { ApiError } from "@/services/api";
import { getApiErrorDescription } from "@/lib/utils/getApiErrorDescription";
import { isGeolocationError } from "@/lib/utils/isGeolocationError";
import {
  DEVICE_TYPE_DESKTOP,
  DEVICE_TYPE_MOBILE,
} from "@/schemas/contractorVisit";

export const useInitializeVisit = (visitId: string) => {
  const { t } = useTranslation();
  const { device } = useDeviceInfo();
  const { toast } = useToast();
  const handleOfflineAwareMutationSuccess =
    useOfflineAwareMutation<ReadContractorVisit>(Module.ContractorPanelVisits);

  return useCachedMutation({
    mutationFn: async () => {
      const { latitude, longitude, accuracyLevel } =
        await GeolocationService.getCurrentPositionWithTimeoutFallback();

      const timeString = DateTime.now().toISO();

      const result = await ContractorPanelVisitService.initializeVisit(
        visitId,
        {
          start_datetime: timeString,
          start_latitude: latitude ?? 0,
          start_longitude: longitude ?? 0,
          device_type:
            device.type === DEVICE_TYPE_DESKTOP
              ? DEVICE_TYPE_DESKTOP
              : DEVICE_TYPE_MOBILE,
          start_geolocation_accuracy: accuracyLevel,
        },
      );

      return { response: result, accuracyLevel };
    },
    onSuccess: ({ response, accuracyLevel }) => {
      const baseMessage = t("contractorVisits.success.visitInitialized");
      const successMessage = getSuccessMessageWithGeolocation(
        baseMessage,
        accuracyLevel,
        t,
      );

      handleOfflineAwareMutationSuccess({
        type: "update",
        response,
        input: response,
        successMessage,
      });
    },
    onError: (error: ApiError) => {
      const apiErrorMessage = getApiErrorDescription(error, t);
      toast({
        title: t("contractorVisits.error.completeFailed"),
        description: isGeolocationError(error)
          ? getGeolocationErrorMessage(error, t)
          : apiErrorMessage,
        variant: "destructive",
      });
    },
  });
};
